// src/components/CustomSideBar.tsx
import React, { useReducer } from "react";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Collapse, IconButton, Link, List, ListItem, Tooltip } from "@mui/material";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import AppButton from "@/components/CustomButton";
import { useRolePermissions } from "@/hooks/useRolePermissions";
import { AuthService } from "@/services";
import { RootState } from "@/stores";
import { logout } from "@/stores/authSlice";
import { SidebarItem, SuperVisor } from "@/types";
import { openDialerForUser } from "@/utils/dialer";
import { Reducer, ReducerAction, setField, setFields } from "@/utils/helpers";

// Styled components for the Sidebar.
// Three fixed zones: toggle row on top, scrollable menu in the middle,
// logout pinned at the bottom — only the menu list scrolls.
const SidebarContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '248px',
  height: '100%',
  flexShrink: 0,
  backgroundColor: '#DEECFF',
  boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.25)',
  maxHeight: '100%',
  overflow: 'hidden',
  transition: 'width 300ms',
  // Collapsed rail: icons only, labels/arrows hidden, logout shrinks to icon.
  '&.smallSidebar': {
    width: 74,
    '& .MuiListItemButton-root': {
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
      justifyContent: 'center',
    },
    '& .MuiListItemIcon-root': { marginRight: 0 },
    '& .MuiListItemText-root': { display: 'none' },
    '& .expandArrow': { display: 'none' },
    // Collapsed rail: hide the wordmark, center the toggle.
    '& .sidebarLogo': { justifyContent: 'center', padding: theme.spacing(1, 1.5) },
    '& .sidebarLogo .logo': { display: 'none' },
    '& .logBtn .MuiButtonBase-root': {
      fontSize: 0,
      minWidth: 1,
      padding: theme.spacing(1.25),
      '& .MuiButton-icon': { margin: 0 },
    },
    '& + .MuiBox-root': { maxWidth: 'calc(100% - 74px)' },
  },
}));

// Fixed row on top holding the logo + collapse toggle (mirrors the agent rail).
// White with the same bottom border as the header, so the logo strip reads as
// one continuous bar with the header beside it.
const SidebarHead = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  flexShrink: 0,
  minHeight: 74,
  padding: theme.spacing(1, 2.5),
  gap: theme.spacing(0.5),
  backgroundColor: '#fff',
  borderBottom: '1px solid #DADADA',
  '& .logo': {
    display: 'inline-flex',
    cursor: 'pointer',
    '& img': { maxWidth: '100%' },
  },
}));

// The only scrollable zone, with a slim scrollbar (same palette, lighter-weight).
const SidebarMenuArea = styled(Box)(() => ({
  flex: 1,
  minHeight: 0,
  width: '100%',
  overflowY: 'auto',
  overflowX: 'hidden',
  scrollbarWidth: 'thin',
  scrollbarColor: '#B9CBEC transparent',
  '&::-webkit-scrollbar': { width: 6 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: '#B9CBEC', borderRadius: 8 },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9DB6E6' },
}));

const SidebarList = styled(List)(() => ({
  padding: 0,
  width: '100%',
}));

const SidebarListItemButton = styled(ListItemButton)(({ theme }) => ({
  padding: theme.spacing(2.25, 2.5),
  color: '#5A5A5A',
  transition: '500ms',
  position: 'relative',
  '&:hover': {
    backgroundColor: '#F5F9FF',
    '& .MuiListItemIcon-root': {
      filter: 'inherit',
      opacity: 1,
    },
    '& .MuiListItemText-primary': {
      color: '#002253',
      fontWeight: 500,
    },
  },
  '&.selected': {
    backgroundColor: '#F5F9FF',
    // Left accent so the active item reads clearly against the light rail
    // (the fill alone is almost invisible on the #DEECFF background).
    boxShadow: 'inset 3px 0 0 #002F73',
    '& .MuiListItemIcon-root': {
      filter: 'inherit',
      opacity: 1,
    },
    '& .MuiListItemText-primary': {
      color: '#002253',
      fontWeight: 500,
    },
  },
  '&.no-filter .MuiListItemIcon-root': {
    filter: 'none',
  },
}));

const SidebarListItemIcon = styled(ListItemIcon)(({ theme }) => ({
  width: 24,
  minWidth: 24,
  marginRight: theme.spacing(1.25),
  filter: 'grayscale(1)',
  opacity: 0.6,
  transition: '500ms',
}));

const SidebarListItemText = styled(ListItemText)(() => ({
  margin: 0,
  // padding: 0,
  // '& .MuiListItemText-primary': {
  //   fontSize: 17,
  //   lineHeight: '23px',
  //   fontWeight: 400,
  //   transition: '500ms',
  // },
}));

const NavLogoutBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  flexShrink: 0,
  padding: theme.spacing(2),
  marginTop: 'auto',
  borderTop: '1px solid rgba(0, 0, 0, 0.08)',
}));

interface TenantSidebarState {
  open: string;
  collapsed: boolean;
}

interface TenantSideBarProps {
  mobile?: boolean;
  onClose?: () => void;
  onNavigate?: () => void;
}

const initialTenantSidebarState: TenantSidebarState = {
  open: "",
  collapsed: false,
};

const TenantSideBar: React.FC<TenantSideBarProps> = ({
  mobile = false,
  onClose,
  onNavigate,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    isSalesManager,
    isSupportManager,
    isGroupMember,
    isGroupUser,
  } = useRolePermissions<SuperVisor | null>();
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const [state, stateDispatch] = useReducer(Reducer, initialTenantSidebarState) as [
    TenantSidebarState,
    React.Dispatch<ReducerAction>,
  ];
  const { collapsed, open } = state;

  const sidebarToggle = () => {
    stateDispatch(setFields({ collapsed: !collapsed, open: "" }));
  };

  const navigateTo = (path: string) => {
    navigate(path);
    onNavigate?.();
  };

  const handleToggle = (label: string) => {
    // Opening a submenu from the collapsed rail expands the sidebar first.
    if (collapsed) {
      stateDispatch(setFields({ collapsed: false, open: label }));
      return;
    }
    stateDispatch(setField("open", label === open ? "" : label));
  };

  // Function to check active status
  const isActive = (path?: string) => {
    if (!path) return false;
    return location.pathname === path;
  };

  const handleLogout = () => {
    AuthService.logout({ token: localStorage.getItem('fcmToken') })
    dispatch(logout());
    navigate("/login");
  }

  const redirectToDialer = () => openDialerForUser(user, accessToken);


  const menuItems: SidebarItem[] = [
    { label: "Dashboard", icon: "/images/dashboard-icon.svg", path: isSalesManager ? "/sales-dashboard" : "/dashboard" },
    { label: "Subscription", icon: "/images/subscription-icon.svg", path: "/subscription" },
    {
      label: "Tickets Feature",
      icon: "/images/tickets-icon.svg",
      children: [
        { label: "Tickets", icon: "/images/group-ticket.svg", path: "/tickets" },
        { label: "Categories", icon: "/images/ticket-logs.svg", path: "/ticket-categories" },
        { label: "Sub Categories", icon: "/images/ticket-sub.svg", path: "/ticket-sub-categories" },
        { label: "Sub-Sub Categories", icon: "/images/ticket-sub-sub.svg", path: "/ticket-sub-sub-categories" },
      ],
    },
    {
      label: "Agent Feature",
      icon: "/images/agent-menu-icon.svg",
      children: [
        { label: "Agents", icon: "/images/agent-headphone-icon.svg", path: "/agents" },
        { label: "Agent Enquiry", icon: "/images/agent-inquiry-icon.svg", path: "/agents-enquery" },
      ],
    },
    {
      label: "Groups Feature",
      icon: "/images/segments-icon.svg",
      children: [
        { label: "Groups", icon: "/images/segments-icon.svg", path: "/groups" },
        ...(isGroupMember
          ? [{ label: "Tickets", icon: "/images/group-ticket.svg", path: "/group-ticket" }]
          : []),
        { label: "Ticket Queue", icon: "/images/tickets-icon.svg", path: "/groups/tickets" },
        { label: "Members", icon: "/images/agent-icon.svg", path: "/groups/members" },
      ]
    },

    { label: "Managers", icon: "/images/manager.svg", path: "/managers" },
    { label: "General Enquiry", icon: "/images/customer-inquiry-icon.svg", path: "/customer-query" },
    { label: "Knowledge base", icon: "/images/knowledge-icon.svg", path: "/knowledge-base" },
    {
      label: "SLA Feature",
      icon: "/images/settings-icon.svg",
      children: [
        { label: "SLA Overview", icon: "/images/dashboard-icon.svg", path: "/settings/sla-overview" },
        { label: "SLA Settings", icon: "/images/settings-icon.svg", path: "/settings/sla-settings" },
      ],
    },
    {
      label: "Facebook",
      icon: "/images/facebook-icon.svg",
      children: [
        { label: "Conversations", icon: "/images/chatbot-icon.svg", path: "/facebook-conversations" },
        { label: "Post Threads", icon: "/images/facebook-icon.svg", path: "/facebook-post-threads" },
        { label: "Templates", icon: "/images/facebook-icon.svg", path: "/facebook-templates" },
      ],
    },
    {
      label: "Instagram",
      icon: "/images/instagram.svg",
      children: [
        { label: "Conversations", icon: "/images/chatbot-icon.svg", path: "/instagram-conversations" },
        { label: "Post Threads", icon: "/images/instagram.svg", path: "/instagram-post-threads" },
        { label: "Templates", icon: "/images/instagram.svg", path: "/instagram-templates" },
      ],
    },
    {
      label: "WhatsApp",
      icon: "/images/whatsapp-icon.svg",
      children: [
        { label: "Conversations", icon: "/images/chatbot-icon.svg", path: "/whatsapp-conversations" },
        { label: "Setup", icon: "/images/whatsapp-icon.svg", path: "/settings/whatsapp" },
      ],
    },
    {
      label: "Reports",
      icon: "/images/survey-icon.svg",
      children: [
        { label: "Agent Call Activity Report", icon: "/images/call-logs.svg", path: "/agents-report" },
        // { label: "Agent Performance Report", icon: "/images/chatbot-icon.svg", path: "/agents-performance-report" },
        { label: "Agent Ticket Handling Report", icon: "/images/ticket-logs.svg", path: "/agents-ticket-report" },
        { label: "Agent Attendance Report", icon: "/images/attendance-icon.svg", path: "/agents-attendance-report", },
        { label: "Csat Report", icon: "/images/csat-star.svg", path: "/csat-report" },
        { label: "Marketing Report", icon: "/images/marketing-report.svg", path: "/marketing-report" },
        { label: "Sales Report", icon: "/images/marketing-report.svg", path: "/sales-report" },
        { label: "Ticket & Analytics Report", icon: "/images/ticket-logs.svg", path: "/ticket-call-analytics" },
        { label: "Agent Analytics Report", icon: "/images/agent-icon.svg", path: "/agent-analytics-report" },
        ...(!isGroupMember
          ? [{ label: "Group Ticket Report", icon: "/images/marketing-report.svg", path: "/ticket-volume-report" }]
          : []),
      ]
    },
    {
      label: "Sales Pipeline Feature", icon: "/images/sales-pipeline-icon.svg",
      children: [
        // ...(isSalesManager ? [] : [{ label: "Dashboard", icon: "/images/dashboard-icon.svg", path: "/sales-dashboard" }]),
        { label: "Sales", icon: "/images/sales-icon.svg", path: "/crm/sales" },
        { label: "Leads", icon: "/images/leads-icon2.svg", path: "/crm/leads" },
        { label: "Category", icon: "/images/category-icon.svg", path: "/crm/category" },
        { label: "Sub Category", icon: "/images/sub-category-icon.svg", path: "/crm/sub-category" },
        { label: "Region", icon: "/images/region-icon.svg", path: "/crm/region" },
        { label: "Sales Enquiry", icon: "/images/sales-query.svg", path: "/crm/sales-query" },
      ]
    },
    {
      label: "CRM", icon: "/images/crm-icon.svg",
      children: [
        { label: "Customer", icon: "/images/work-force-icon.svg", path: "/crm/customers" },
        { label: "Requests", icon: "/images/change-request-icon.svg", path: "/crm/requests" },
      ]
    },
    {
      label: "Marketing", icon: "/images/marketing-icon.svg",
      children: [
        { label: "Email Templates", icon: "/images/email-solid-icon.svg", path: "/email/templates" },
        { label: "Prospects", icon: "/images/prospects-icon.svg", path: "/prospects" },
        { label: "Segments", icon: "/images/segments-icon.svg", path: "/segments" },
        { label: "Campaigns", icon: "/images/email-campaign-icon.svg", path: "/campaigns" },
      ]
    },

    {
      label: "Work Force", icon: "/images/work-force-icon.svg",
      children: [
        { label: "Approvals", icon: "/images/work-shift-icon.svg", path: "/workforce-overview" },
        { label: "Shift Management", icon: "/images/attendance-icon.svg", path: "/shift-management" },
        { label: "Rules And Settings", icon: "/images/work-force-icon.svg", path: "/rules-settings" },
        { label: "Shift Calendar", icon: "/images/calendar-icon.svg", path: "/shift-calendar" },
        { label: "Staff Overview", icon: "/images/calendar-icon.svg", path: "/staff-list" },
        // { label: "Optimization", icon: "/images/work-force-icon.svg", path: "/optimization" },
      ]
    },

    {
      label: "Settings",
      icon: "/images/settings-new-icon.svg",
      children: [
        { label: "ChatBot", icon: "/images/chatbot-icon.svg", path: "/settings/chatbot-questions" },
        { label: "Ticket", icon: "/images/tickets-icon.svg", path: "/settings/ticketing-system" },
        { label: "Global", icon: "/images/global-icon.svg", path: "/settings/global" },
        { label: "Custom Scripts", icon: "/images/text-icon.svg", path: "/settings/custom-scripts" },
        { label: "Dialer", icon: "/images/dialer-icon.svg", path: "/Dialer", onClick: redirectToDialer, },
      ]
    },
    {
      label: "Retention",
      icon: "/images/mage-archive.svg",
      children: [
        { label: "Policies", icon: "/images/settings-icon.svg", path: "/retention/policies" },
        { label: "Archived Records", icon: "/images/mage-archive.svg", path: "/retention/archive-records" },
        { label: "Legal Holds", icon: "/images/change-request-icon.svg", path: "/retention/legal-holds" },
        { label: "Audit Trail", icon: "/images/text-icon.svg", path: "/retention/audit-trail" },
      ]
    },
    {
      label: "Auxiliary",
      icon: "/images/handshake-icon.svg",
      children: [
        { label: "API", icon: "/images/api-icon.svg", path: "/api-docs" },
      ]
    },
    { label: "Feedback", icon: "/images/feedback-icon.svg", path: "/feedback" },
    // { label: "AI Assistant", icon: "/images/ai-assistant-icon.svg", path: "/ai-assistant" },
    // { label: "Reviews", icon: "/images/reviews-icon.svg", path: "/reviews" },
    // { label: "Training", icon: "/images/training-icon.svg", path: "/training" },
    // { label: "Escalation", icon: "/images/escalation-icon.svg", path: "/escalation" },
  ];

  // Map route path -> API menu id
  const pathMenuIdMap: Record<string, number> = {
    '/dashboard': 1,
    '/tickets': 2,
    '/customer-query': 4,
    '/ticket-categories': 47,
    '/ticket-sub-categories': 47,
    '/ticket-sub-sub-categories': 47,
    '/subscription': 21,
    '/agents': 22,
    '/groups': 58,
    '/groups/details/:groupId': 58,
    '/groups/members': 59,
    '/groups/tickets': 60,
    '/groups/tickets/:id': 60,
    '/group-ticket': 63,
    '/group-ticket/:id': 63,
    '/ticket-volume-report': 61,
    '/time-in-group-report': 62,
    '/agents-enquery': 23,
    '/knowledge-base': 24,
    // Reports children
    '/agents-report': 25,
    '/agents-ticket-report': 26,
    '/agents-attendance-report': 27,
    '/sales-report': 28,
    '/csat-report': 48,
    '/ticket-call-analytics': 1,
    '/agent-analytics-report': 1,
    // Sales Pipeline children (use path to avoid label collision with top-level Dashboard)
    '/sales-dashboard': 28,
    '/crm/category': 29,
    '/crm/sub-category': 30,
    '/crm/region': 31,
    '/crm/sales-query': 12,
    '/crm/sales': 7,
    '/crm/leads': 10,
    // CRM children
    '/crm/customers': 6,
    '/crm/requests': 16,
    // Marketing children
    '/email/templates': 17,
    '/prospects': 18,
    '/segments': 19,
    '/campaigns': 20,
    // Work Force children have no specific menu id in API, show based on parent
    // Settings children
    '/settings/chatbot-questions': 32,
    '/settings/ticketing-system': 33,
    '/settings/global': 34,
    '/settings/custom-scripts': 35,
    '/settings/sla-overview': 36,
    '/settings/sla-settings': 36,
    '/settings/whatsapp': 37,
    '/Dialer': 3,
    // id 38 - Dialer (onClick, no path)
    // Auxiliary
    '/api-docs': 39,
    '/feedback': 40,
    '/managers': 41,
    '/facebook-post-threads': 49,
    '/facebook-post-threads/:threadId': 49,
    '/facebook-conversations': 49,
    '/facebook-conversations/:conversationId': 49,
    '/facebook-templates': 49,
    '/marketing-report': 50,

    '/workforce-overview': 42,
    '/all-leave-requests': 42,
    '/all-swap-requests': 42,
    '/shift-management': 43,
    '/rules-settings': 44,
    '/shift-calendar': 45,
    '/staff-list': 46,
    '/instagram-post-threads': 52,
    '/instagram-post-threads/:threadId': 52,
    '/instagram-conversations': 52,
    '/instagram-conversations/:conversationId': 52,
    '/instagram-templates': 52,
    '/whatsapp-conversations': 37,
    '/whatsapp-conversations/:conversationId': 37,
    // Retention (id 57)
    '/retention/policies': 57,
    '/retention/archive-records': 57,
    '/retention/legal-holds': 57,
    '/retention/audit-trail': 57,
    // '/optimization':56,
  };

  // Top-level items without children mapped by label
  const topLevelLabelMenuIdMap: Record<string, number> = {
    'Dashboard': 1,
    'Tickets': 2,
    'Subscription': 21,
    'Agents': 22,
    'Agent Enquiry': 23,
    'Knowledge base': 24,
    'Feedback': 40,
  };

  const resolveMenuId = (
    entry: { label: string; path?: string }
  ): number | undefined => {
    if (!entry.path && entry.label === 'Dialer') return 3;
    if (entry.path) return pathMenuIdMap[entry.path] ?? topLevelLabelMenuIdMap[entry.label];
    return topLevelLabelMenuIdMap[entry.label];
  };

  const filterMenuByAssigned = (): SidebarItem[] => {
    const assignMenu = user?.assignMenu;
    const originalMenu = isGroupUser
      ? undefined
      : user?.originalMenu;
    const roleGrantedMenuIds = isSupportManager
      ? [58, 59, 61, 62]
      : [];

    // Fallback: no originalMenu in response → behave as before (show only assigned)
    if (!originalMenu || originalMenu.length === 0) {
      if ((!assignMenu || assignMenu.length === 0) && roleGrantedMenuIds.length === 0) {
        return menuItems;
      }
      const assignedMenuIds = (assignMenu ?? []).map((item) => item.menu.id);
      const combinedReportMenuIds = assignedMenuIds.some((id) => id === 61 || id === 62)
        ? [61, 62]
        : [];
      const allowedMenuIds = [
        ...new Set([
          ...assignedMenuIds,
          ...combinedReportMenuIds,
          ...roleGrantedMenuIds,
        ]),
      ];
      return menuItems
        .map((item) => {
          if (item.children) {
            const filteredChildren = item.children.filter((child) => {
              const id = resolveMenuId(child);
              return id !== undefined && allowedMenuIds.includes(id);
            });
            if (filteredChildren.length === 0) return null;
            return { ...item, children: filteredChildren };
          }
          const id = resolveMenuId(item);
          if (id === undefined) return item;
          return allowedMenuIds.includes(id) ? item : null;
        })
        .filter(Boolean) as SidebarItem[];
    }

    const rawOriginalIds = originalMenu.map((item) => item.menu.id);
    const combinedOriginalReportMenuIds = rawOriginalIds.some((id) => id === 61 || id === 62)
      ? [61, 62]
      : [];
    const originalIds = [
      ...new Set([
        ...rawOriginalIds,
        ...combinedOriginalReportMenuIds,
        ...roleGrantedMenuIds,
      ]),
    ];
    const rawAssignedIds = (assignMenu ?? []).map((item) => item.menu.id);
    const combinedReportMenuIds = rawAssignedIds.some((id) => id === 61 || id === 62)
      ? [61, 62]
      : [];
    const assignedIds = [
      ...new Set([
        ...rawAssignedIds,
        ...combinedReportMenuIds,
        ...roleGrantedMenuIds,
      ]),
    ];
    const isInOriginal = (id?: number) => id !== undefined && originalIds.includes(id);
    const isAssigned = (id?: number) => id !== undefined && assignedIds.includes(id);

    return menuItems
      .map((item) => {
        if (item.children) {
          const visibleChildren = item.children
            .map((child) => {
              const id = resolveMenuId(child);
              if (!isInOriginal(id)) return null;
              return { ...child, disabled: !isAssigned(id) };
            })
            .filter(Boolean) as NonNullable<SidebarItem['children']>;

          if (visibleChildren.length === 0) return null;
          const allDisabled = visibleChildren.every((c) => c.disabled);
          return { ...item, children: visibleChildren, disabled: allDisabled };
        }

        const id = resolveMenuId(item);
        if (id === undefined) return item; // always show unrecognised items
        if (!isInOriginal(id)) return null;
        return { ...item, disabled: !isAssigned(id) };
      })
      .filter(Boolean) as SidebarItem[];
  };

  const filteredMenuItems = filterMenuByAssigned();

  return (
    <SidebarContainer className={collapsed ? 'smallSidebar' : ''}>
      <SidebarHead className="sidebarLogo">
        <Link className="logo" onClick={() => navigateTo('/')}>
          <img src="/images/logo-2x.png" alt="Logo" />
        </Link>
        <Tooltip title={mobile ? 'Close menu' : collapsed ? 'Expand menu' : 'Collapse menu'} placement="right" arrow>
          <IconButton
            onClick={mobile ? onClose : sidebarToggle}
            size="small"
            aria-label={mobile ? 'Close navigation' : 'Toggle sidebar'}
            sx={{ ml: 'auto', mt: 1.5, mr: -0.75 }}
          >
            {mobile ? (
              <CloseIcon sx={{ color: '#5A5A5A' }} />
            ) : collapsed ? (
              <MenuIcon sx={{ color: '#5A5A5A' }} />
            ) : (
              <MenuOpenIcon sx={{ color: '#5A5A5A' }} />
            )}
          </IconButton>
        </Tooltip>
      </SidebarHead>
      <SidebarMenuArea as="nav" aria-label="main mailbox folders" className="dashNavbar">
        <SidebarList>
          {filteredMenuItems.map((item, index) => {
            const hasChildren = Number(item.children?.length) > 0;
            // Parent is interactive if it has children (to expand and show disabled children inside)
            // OR it has a path and is not disabled.
            const parentClickDisabled = !hasChildren && item.disabled;
            const isNoFilter = item.label === 'Facebook' || item.label === 'Instagram' || item.label === 'WhatsApp';
            const parentButton = (
              <SidebarListItemButton
                onClick={() => {
                  if (parentClickDisabled) return;
                  if (hasChildren) {
                    handleToggle(item.label);
                  } else if (item.path) {
                    navigateTo(item.path);
                  }
                }}
                className={`${isActive(item.path) ? "selected" : ""} ${isNoFilter ? "no-filter" : ""}`}
                sx={parentClickDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
              >
                <SidebarListItemIcon>
                  <img src={item.icon} alt={item.label} />
                </SidebarListItemIcon>
                <SidebarListItemText primary={item.label} />
                {hasChildren && (open === item.label ? <ExpandLess className="expandArrow" /> : <ExpandMore className="expandArrow" />)}
              </SidebarListItemButton>
            );

            return (
              <React.Fragment key={index}>
                <ListItem disablePadding sx={{ mt: 1, ...(hasChildren ? { flexWrap: 'wrap' } : {}) }}>
                  {parentClickDisabled ? (
                    <Tooltip title="Contact admin for access" placement="right" arrow>
                      <Box sx={{ width: '100%' }}>{parentButton}</Box>
                    </Tooltip>
                  ) : collapsed ? (
                    <Tooltip title={item.label} placement="right" arrow>
                      <Box sx={{ width: '100%' }}>{parentButton}</Box>
                    </Tooltip>
                  ) : (
                    parentButton
                  )}
                </ListItem>
                {hasChildren && (
                  <Collapse in={open === item.label} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
                    <SidebarList disablePadding>
                      {item.children?.map((child, idx) => {
                        const isChildNoFilter = child.icon.includes('facebook') || child.icon.includes('instagram') || child.icon.includes('whatsapp');
                        const childButton = (
                          <SidebarListItemButton
                            sx={{ pl: 6, pr: 2.5, py: 1.5, ...(child.disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
                            onClick={() => {
                              if (child.disabled) return;
                              if (child?.onClick) child?.onClick()
                              else if (child?.target) window.open(child?.path, child?.target);
                              else if (child.path) navigate(child.path)
                              onNavigate?.();
                            }}
                            className={`${isActive(child.path) ? "selected" : ""} ${isChildNoFilter ? "no-filter" : ""}`}
                          >
                            <SidebarListItemIcon>
                              <img src={child.icon} alt={child.label} />
                            </SidebarListItemIcon>
                            <SidebarListItemText primary={child.label} />
                          </SidebarListItemButton>
                        );
                        return (
                          <ListItem disablePadding sx={{ mt: 1 }} key={idx}>
                            {child.disabled ? (
                              <Tooltip title="Contact admin for access" placement="right" arrow>
                                <Box sx={{ width: '100%' }}>{childButton}</Box>
                              </Tooltip>
                            ) : (
                              childButton
                            )}
                          </ListItem>
                        );
                      })}
                    </SidebarList>
                  </Collapse>
                )}
              </React.Fragment>
            );
          })}
        </SidebarList>
      </SidebarMenuArea>

      <NavLogoutBox className="logBtn">
        <AppButton
          onClick={() => handleLogout()}
          disableElevation
          variant="outlined"
          size="small"
          startIcon={<img src="/images/logout.svg" alt="Logout" />}
        >
          Logout
        </AppButton>
      </NavLogoutBox>
    </SidebarContainer>
  );
};

export default TenantSideBar;
